<script setup>
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import { useRouter } from '#app';

const props = defineProps({
  pagination: { type: Object, required: true, default: () => null },
  around: { type: Number, default: 2 },
  pageParam: { type: String, default: 'page' },
});

const emit = defineEmits(['change']);

const router = useRouter();

const current = computed(() => Number(props.pagination?.current_page || 1));
const last = computed(() => Number(props.pagination?.last_page || 1));

const hasPrev = computed(() => current.value > 1);
const hasNext = computed(() => current.value < last.value);

const currentPathname = ref('');
const currentSearch = ref('');

const syncLocation = () => {
  if (!process.client) return;
  currentPathname.value = window.location.pathname;
  currentSearch.value = window.location.search || '';
};

onMounted(() => {
  syncLocation();
  window.addEventListener('urlchange', syncLocation);
  window.addEventListener('popstate', syncLocation);
});

onBeforeUnmount(() => {
  window.removeEventListener('urlchange', syncLocation);
  window.removeEventListener('popstate', syncLocation);
});

function buildHref(pageNum) {
  if (!process.client) return '#';
  const sp = new URLSearchParams(currentSearch.value);
  sp.delete(props.pageParam);

  if (pageNum > 1) sp.set(props.pageParam, String(pageNum));
  const qs = sp.toString();

  return qs ? `${currentPathname.value}?${qs}` : `${currentPathname.value}`;
}

const pageTokens = computed(() => {
  const c = current.value;
  const l = last.value;
  const a = props.around;

  if (l <= 1) return [1];

  const pages = new Set([1, l]);
  const start = Math.max(1, c - a);
  const end = Math.min(l, c + a);

  for (let p = start; p <= end; p++) pages.add(p);

  const sorted = [...pages].sort((x, y) => x - y);

  const tokens = [];
  let prev = null;
  for (const p of sorted) {
    if (prev != null && p - prev > 1) tokens.push('…');
    tokens.push(p);
    prev = p;
  }
  return tokens;
});

function shouldInterceptClick(e) {
  if (!process.client) return false;
  if (e.defaultPrevented) return false;
  if (e.button !== 0) return false;
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return false;
  return true;
}

function goToPage(e, pageNum) {
  const l = last.value;
  if (!Number.isFinite(pageNum) || pageNum < 1 || pageNum > l) {
    e.preventDefault();
    return;
  }
  if (!shouldInterceptClick(e)) return;

  e.preventDefault();

  const href = buildHref(pageNum);
  // Go through vue-router's own history object (still a synchronous
  // history.replaceState under the hood) instead of window.history
  // directly, so vue-router's internal "current location" bookkeeping
  // doesn't go stale — see the matching comment in useCatalogFilters.js.
  router.options.history.replace(href);

  window.dispatchEvent(new Event('urlchange'));

  emit('change', pageNum);
}
</script>

<template>
  <nav
    v-if="pagination && last > 1"
    class="catalog-pagination"
    aria-label="Pagination"
  >
    <a
      v-if="hasPrev"
      :href="buildHref(current - 1)"
      class="catalog-pagination__btn catalog-pagination__prev"
      :class="{ 'catalog-pagination__btn--disabled': !hasPrev }"
      :aria-disabled="!hasPrev"
      :tabindex="!hasPrev ? -1 : 0"
      rel="prev"
      @click="(e) => (hasPrev ? goToPage(e, current - 1) : e.preventDefault())"
    >
      <CIcon
        class="catalog-pagination__prev-icon"
        name="direction-black"
        width="16"
        height="16"
      />
    </a>
    <span
      v-else
      class="catalog-pagination__btn catalog-pagination__prev catalog-pagination__btn--disabled"
      :aria-disabled="!hasPrev"
      :tabindex="!hasPrev ? -1 : 0"
      rel="prev"
    >
      <CIcon
        class="catalog-pagination__prev-icon"
        name="direction-black"
        width="16"
        height="16"
      />
    </span>

    <ul class="m-0 p-0 catalog-pagination__list">
      <li
        v-for="(t, idx) in pageTokens"
        :key="`${t}-${idx}`"
        class="catalog-pagination__list-item"
      >
        <span
          v-if="t === '…'"
          class="catalog-pagination__ellipsis"
          aria-hidden="true"
          >…</span
        >
        <a
          v-else
          :href="buildHref(t)"
          class="catalog-pagination__page"
          :class="{ 'catalog-pagination__page--active': t === current }"
          :aria-current="t === current ? 'page' : null"
          @click="(e) => goToPage(e, t)"
        >
          {{ t }}
        </a>
      </li>
    </ul>
    <a
      v-if="hasNext"
      :href="buildHref(current + 1)"
      class="catalog-pagination__btn catalog-pagination__next"
      :class="{ 'catalog-pagination__btn--disabled': !hasNext }"
      :aria-disabled="!hasNext"
      :tabindex="!hasNext ? -1 : 0"
      rel="next"
      @click="(e) => (hasNext ? goToPage(e, current + 1) : e.preventDefault())"
    >
      <CIcon
        class="catalog-pagination__next-icon"
        name="direction-black"
        width="16"
        height="16"
      />
    </a>
    <span
      v-else
      class="catalog-pagination__btn catalog-pagination__next catalog-pagination__btn--disabled"
      :aria-disabled="!hasNext"
      :tabindex="!hasNext ? -1 : 0"
      rel="next"
    >
      <CIcon
        class="catalog-pagination__next-icon"
        name="direction-black"
        width="16"
        height="16"
      />
    </span>
  </nav>
</template>
<style scoped lang="scss">
.catalog-pagination {
  border-top: 1px solid $divider;
  align-items: center;
  display: flex;
  justify-content: center;
  gap: 12px;
  padding: 25px 16px 24px;
  @include media-breakpoint-up(sm) {
    gap: 28px;
    padding: 41px 48px 40px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 28);
    @include responsive-spacing(padding-top, 41);
    @include responsive-spacing(padding-left, 48);
    @include responsive-spacing(padding-right, 48);
    @include responsive-spacing(padding-bottom, 40);
  }
  &__list {
    align-items: center;
    display: flex;
    gap: 4px;
    list-style: none;
    margin: 0;
    padding: 0;
    @include media-breakpoint-up(sm) {
      gap: 12px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 12);
    }
    &-item {
      margin: 0;
      padding: 0;
    }
  }
  &__btn {
    align-items: center;
    display: flex;
    justify-content: center;
    height: 32px;
    width: 32px;
    @include media-breakpoint-up(sm) {
      border: 1px solid $outline !important;
      border-radius: 50%;
      height: 48px;
      width: 48px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-width, 1);
      @include responsive-spacing(height, 48);
      @include responsive-spacing(width, 48);
    }
    &--disabled {
      pointer-events: none;
      opacity: 0.5;
    }
  }
  &__next {
    margin-left: auto;
  }
  &__prev {
    margin-right: auto;
    &-icon {
      transform: rotate(180deg);
    }
  }
  &__page {
    @include link-normal;
    align-items: center;
    border-radius: 50%;
    display: inline-flex;
    justify-content: center;
    height: 32px;
    min-width: 24px;
    text-decoration: none;
    @include media-breakpoint-up(sm) {
      border: 1px solid $divider;
      height: 48px;
      min-width: 48px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-width, 1);
      @include responsive-spacing(height, 48);
      @include responsive-spacing(min-width, 48);
    }
    &--active {
      background-color: $black;
      border-radius: 50%;
      color: $white;
      min-width: 32px;
      @include media-breakpoint-up(sm) {
        border: 1px solid $black;
        min-width: 48px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-width, 1);
        @include responsive-spacing(min-width, 48);
      }
    }
  }
  &__ellipsis {
    @include link-normal;
    display: inline-flex;
    min-width: 24px;
    justify-content: center;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(min-width, 24);
    }
  }
}
</style>
